import { banco } from '../db/banco'
import type { SubPedido } from '../db/tipos'
import { STATUS_VENDA } from '../metas/statusVenda'
import { dentroDoPeriodo } from '../metas/calculo'
import { unidadesTotais } from './embalagem'

export interface VendaDoProduto {
  pedidoId: string
  numero: string
  data: string
  clienteId: string
  clienteNome: string
  clienteDocumento: string | null
  unidades: number
  valorTotal: number
  status: SubPedido['status']
}

// O que conta como VENDA e a mesma regra de historicoDoProduto.ts: pedido
// fechado, sub-pedido em STATUS_VENDA (rascunho e cancelado ficam de fora).
export async function carregarVendasDoProduto(produtoId: string): Promise<VendaDoProduto[]> {
  const itens = await banco.itens_pedido
    .where('produto_id').equals(produtoId)
    .filter((i) => i.apagado_em === null)
    .toArray()
  const vendas: VendaDoProduto[] = []
  for (const item of itens) {
    const sub = await banco.sub_pedidos.get(item.sub_pedido_id)
    if (!sub || sub.apagado_em !== null || !STATUS_VENDA.includes(sub.status)) continue
    const pedido = await banco.pedidos.get(sub.pedido_id)
    if (!pedido || pedido.apagado_em !== null || pedido.status !== 'fechado') continue
    const cliente = await banco.clientes.get(pedido.cliente_id)
    vendas.push({
      pedidoId: pedido.id,
      numero: pedido.numero,
      data: pedido.data,
      clienteId: pedido.cliente_id,
      clienteNome: cliente ? (cliente.nome_fantasia || cliente.razao_social) : '',
      clienteDocumento: cliente?.cnpj_cpf ?? null,
      unidades: unidadesTotais(item),
      valorTotal: item.total_item,
      status: sub.status,
    })
  }
  return vendas.sort((a, b) => (a.data === b.data ? b.numero.localeCompare(a.numero) : a.data < b.data ? 1 : -1))
}

export function filtrarVendas(
  vendas: VendaDoProduto[], de: string, ate: string, clienteId: string | null,
): VendaDoProduto[] {
  return vendas.filter(
    (v) => dentroDoPeriodo(v.data, de, ate) && (clienteId === null || v.clienteId === clienteId),
  )
}

export interface ResumoDeVendas {
  total: number
  unidades: number
  clientes: number
  ticketMedio: number | null
}

export function resumirVendas(vendas: VendaDoProduto[]): ResumoDeVendas {
  const total = vendas.reduce((s, v) => s + v.valorTotal, 0)
  const pedidos = new Set(vendas.map((v) => v.pedidoId)).size
  return {
    total,
    unidades: vendas.reduce((s, v) => s + v.unidades, 0),
    clientes: new Set(vendas.map((v) => v.clienteId)).size,
    ticketMedio: pedidos === 0 ? null : total / pedidos,
  }
}

export interface BarraDoMes {
  chave: string
  rotulo: string
  total: number
}

// Barras por MES (decisao dela, 23/09): com 149 itens no banco inteiro, barra
// por dia ficaria quase toda vazia. Os meses vem de quem chama -- no Historico,
// os 6 do semestre escolhido (rodada 2, 24/09 tarde: antes eram fixos nos ultimos 6).
export function vendasPorMes(
  vendas: VendaDoProduto[], clienteId: string | null, chaves: string[],
): BarraDoMes[] {
  return chaves.map((chave) => {
    const [a, m] = chave.split('-').map(Number)
    const rotulo = new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')
    const total = vendas
      .filter((v) => v.data.startsWith(chave) && (clienteId === null || v.clienteId === clienteId))
      .reduce((s, v) => s + v.valorTotal, 0)
    return { chave, rotulo, total }
  })
}

export function clientesQueCompraram(vendas: VendaDoProduto[]): Array<{ id: string; nome: string }> {
  const porId = new Map(vendas.map((v) => [v.clienteId, v.clienteNome]))
  return [...porId].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
}
